import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "./vehiclePrice";

/** Query keys on /inventory, in the order a shared link always uses. */
export const PARAM_ORDER = [
  "make",
  "model",
  "trim",
  "keyword",
  "condition",
  "body",
  "body_type",
  "fuel_type",
  "transmission",
  "drivetrain",
  "cylinders",
  "exterior_color",
  "interior_color",
  "min_price",
  "max_price",
  "min_year",
  "max_year",
  "min_mileage",
  "max_mileage",
  "sort",
  "page",
  "per_page",
];

export const DEFAULT_SORT = "price-desc";
export const SORTS = ["price-asc", "price-desc", "year-desc", "mileage-asc"];

/** Body groups the chatbot and the inventory page share. */
export const BODY_GROUPS = [
  { value: "suv", labelKey: "inventory.bodyGroups.suv" },
  { value: "sedan", labelKey: "inventory.bodyGroups.sedan" },
  { value: "truck", labelKey: "inventory.bodyGroups.truck" },
  { value: "pickup", labelKey: "inventory.bodyGroups.pickup" },
  { value: "hatchback", labelKey: "inventory.bodyGroups.hatchback" },
  { value: "coupe", labelKey: "inventory.bodyGroups.coupe" },
  { value: "van", labelKey: "inventory.bodyGroups.van" },
  { value: "camioneta", labelKey: "inventory.bodyGroups.camioneta" },
];

const BODY_GROUP_VALUES = BODY_GROUPS.map((item) => item.value);

const rangeOf = (options, key, fallbackMin, fallbackMax) => {
  const range = options && options[key] ? options[key] : {};
  const min = Number(range.min);
  const max = Number(range.max);
  return {
    min: Number.isFinite(min) ? min : fallbackMin,
    max: Number.isFinite(max) ? max : fallbackMax,
  };
};

export const emptyFilters = (options) => {
  const price = rangeOf(options, "price_range", 0, 100000);
  const year = rangeOf(options, "year_range", 2020, 2025);
  const mileage = rangeOf(options, "mileage_range", 0, 200000);
  return {
    make: "",
    model: "",
    trim: "",
    condition: "",
    bodyGroup: "",
    bodyType: "",
    fuelType: "",
    transmission: "",
    drivetrain: "",
    cylinders: "",
    exteriorColor: "",
    interiorColor: "",
    minPrice: price.min,
    maxPrice: price.max,
    minYear: year.min,
    maxYear: year.max,
    minMileage: mileage.min,
    maxMileage: mileage.max,
    searchQuery: "",
  };
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const readInt = (params, key) => {
  if (!params.has(key)) return null;
  const parsed = parseInt(String(params.get(key)).replace(/[^0-9-]/g, ""), 10);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Match a URL value to a dropdown option. Exact case-insensitive match wins. */
export const matchChoice = (raw, options) => {
  const text = String(raw || "").trim();
  if (!text) return "";
  const list = (options || []).map((opt) => String(opt));
  const exact = list.find((opt) => opt.toLowerCase() === text.toLowerCase());
  if (exact) return exact;
  const partial = list.filter((opt) => opt.toLowerCase().includes(text.toLowerCase()));
  return partial.length === 1 ? partial[0] : "";
};

const snapYear = (value, years, fallback) => {
  const list = (years || []).map((year) => Number(year)).filter((year) => Number.isFinite(year));
  if (!list.length || !Number.isFinite(value)) return fallback;
  if (list.includes(value)) return value;
  return list.reduce((best, year) => (Math.abs(year - value) < Math.abs(best - value) ? year : best));
};

const asParams = (search) => {
  if (search instanceof URLSearchParams) return search;
  return new URLSearchParams(String(search || "").replace(/^\?/, ""));
};

export const stateFromSearchParams = (search, options) => {
  const params = asParams(search);
  const filters = emptyFilters(options);
  const price = rangeOf(options, "price_range", 0, 100000);
  const year = rangeOf(options, "year_range", 2020, 2025);
  const mileage = rangeOf(options, "mileage_range", 0, 200000);

  filters.make = matchChoice(params.get("make"), options && options.makes);
  filters.model = matchChoice(params.get("model"), options && options.models);
  filters.trim = matchChoice(params.get("trim"), options && options.trims);
  filters.condition = matchChoice(params.get("condition"), options && options.conditions);
  filters.fuelType = matchChoice(params.get("fuel_type"), options && options.fuel_types);
  filters.transmission = matchChoice(params.get("transmission"), options && options.transmissions);
  filters.drivetrain = matchChoice(params.get("drivetrain"), options && options.drivetrains);
  filters.cylinders = matchChoice(params.get("cylinders"), options && options.cylinders);
  filters.exteriorColor = matchChoice(params.get("exterior_color"), options && options.exterior_colors);
  filters.interiorColor = matchChoice(params.get("interior_color"), options && options.interior_colors);
  filters.searchQuery = String(params.get("keyword") || "").trim();

  let body = String(params.get("body") || "").trim().toLowerCase();
  if (body === "crossover") body = "suv";
  if (BODY_GROUP_VALUES.includes(body)) {
    filters.bodyGroup = body;
    filters.bodyType = "";
  } else {
    filters.bodyType = matchChoice(params.get("body_type"), options && options.body_types);
  }

  const minPrice = readInt(params, "min_price");
  const maxPrice = readInt(params, "max_price");
  const minMileage = readInt(params, "min_mileage");
  const maxMileage = readInt(params, "max_mileage");
  filters.minPrice = minPrice == null ? price.min : clamp(minPrice, price.min, price.max);
  filters.maxPrice = maxPrice == null ? price.max : clamp(maxPrice, price.min, price.max);
  if (filters.minPrice > filters.maxPrice) {
    filters.minPrice = price.min;
    filters.maxPrice = price.max;
  }
  filters.minMileage = minMileage == null ? mileage.min : clamp(minMileage, mileage.min, mileage.max);
  filters.maxMileage = maxMileage == null ? mileage.max : clamp(maxMileage, mileage.min, mileage.max);
  if (filters.minMileage > filters.maxMileage) {
    filters.minMileage = mileage.min;
    filters.maxMileage = mileage.max;
  }

  const minYear = readInt(params, "min_year");
  const maxYear = readInt(params, "max_year");
  filters.minYear = snapYear(minYear == null ? year.min : minYear, options && options.years, year.min);
  filters.maxYear = snapYear(maxYear == null ? year.max : maxYear, options && options.years, year.max);
  if (filters.minYear > filters.maxYear) {
    filters.minYear = year.min;
    filters.maxYear = year.max;
  }

  const sortRaw = params.get("sort");
  const pageRaw = parseInt(params.get("page") || "1", 10);
  const sizeRaw = parseInt(params.get("per_page") || "", 10);
  return {
    filters,
    sort: SORTS.includes(sortRaw) ? sortRaw : DEFAULT_SORT,
    page: Number.isFinite(pageRaw) && pageRaw > 1 ? pageRaw : 1,
    pageSize: PAGE_SIZE_OPTIONS.includes(sizeRaw) ? sizeRaw : DEFAULT_PAGE_SIZE,
  };
};

export const queryFromState = ({ filters, options, sort, page, pageSize }) => {
  const current = filters || emptyFilters(options);
  const price = rangeOf(options, "price_range", 0, 100000);
  const year = rangeOf(options, "year_range", 2020, 2025);
  const mileage = rangeOf(options, "mileage_range", 0, 200000);
  const values = {
    make: current.make || "",
    model: current.model || "",
    trim: current.trim || "",
    keyword: (current.searchQuery || "").trim(),
    condition: current.condition || "",
    body: current.bodyGroup || "",
    body_type: current.bodyGroup ? "" : (current.bodyType || ""),
    fuel_type: current.fuelType || "",
    transmission: current.transmission || "",
    drivetrain: current.drivetrain || "",
    cylinders: current.cylinders || "",
    exterior_color: current.exteriorColor || "",
    interior_color: current.interiorColor || "",
    min_price: current.minPrice > price.min ? current.minPrice : "",
    max_price: current.maxPrice < price.max ? current.maxPrice : "",
    min_year: current.minYear > year.min ? current.minYear : "",
    max_year: current.maxYear < year.max ? current.maxYear : "",
    min_mileage: current.minMileage > mileage.min ? current.minMileage : "",
    max_mileage: current.maxMileage < mileage.max ? current.maxMileage : "",
    sort: sort && sort !== DEFAULT_SORT ? sort : "",
    page: page > 1 ? page : "",
    per_page: pageSize && pageSize !== DEFAULT_PAGE_SIZE ? pageSize : "",
  };
  const params = new URLSearchParams();
  PARAM_ORDER.forEach((key) => {
    if (values[key] === undefined || values[key] === null || values[key] === "") return;
    params.append(key, String(values[key]));
  });
  return params.toString();
};
